import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { recordInvoicePrint, type InvoiceDetail } from './invoiceApi'
import { invoiceImagesSettled } from './invoicePrint'
import { InvoicePrintPortal } from './InvoiceSheet'

export function useInvoicePrinter() {
  const queryClient = useQueryClient()
  const [job, setJob] = useState<{ invoice: InvoiceDetail; copy: boolean } | null>(null)
  const [printing, setPrinting] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (job === null) return
    let cancelled = false
    const run = async () => {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await invoiceImagesSettled()
      if (cancelled) return
      window.print()
      setJob(null)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [job])

  const print = useCallback(
    async (invoice: InvoiceDetail) => {
      setError(null)
      setPrinting(true)
      try {
        const { copy } = await recordInvoicePrint(invoice.id)
        setJob({ invoice, copy })
        void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(invoice.id) })
      } catch (failure) {
        setError(failure)
      } finally {
        setPrinting(false)
      }
    },
    [queryClient],
  )

  return {
    print,
    printing,
    error,
    portal: job ? <InvoicePrintPortal invoice={job.invoice} copy={job.copy} /> : null,
  }
}
